import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import type { IInvoice } from "../../invoice/create/interfaces/invoice.interfaces";
import { getDepositInvoicesByQuote, reissueDepositInvoice } from "../actions/depositInvoices";

const DEPOSIT_INVOICES_KEY = (quoteId: number | undefined) => ["quotes", quoteId, "deposit-invoices"];

const toMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) return (error.response?.data as any)?.message || error.message || fallback;
  if (error instanceof Error) return error.message;
  return fallback;
};

export const useDepositInvoicesByQuote = (quoteId: number | undefined) =>
  useQuery<IInvoice[]>({
    queryKey: DEPOSIT_INVOICES_KEY(quoteId),
    queryFn: () => getDepositInvoicesByQuote(quoteId!),
    enabled: Number.isFinite(quoteId),
  });

export const useReissueDepositInvoice = (quoteId: number | undefined) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useGlobalSnackbar();

  const mutation = useMutation({
    mutationFn: (payload?: { depositAmount?: number }) => {
      if (!quoteId) throw new Error("Missing quote id");
      return reissueDepositInvoice(quoteId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEPOSIT_INVOICES_KEY(quoteId) });
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("flownect:quotes-updated", { detail: { quoteId } }));
      }
      showSnackbar("Deposit invoice issued", "success", 3000, "top-center");
    },
    onError: (error) => {
      showSnackbar(toMessage(error, "Could not issue deposit invoice"), "error", 6000, "top-center");
    },
  });

  return { reissue: mutation.mutate, isPending: mutation.isPending };
};
